import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { UploadSimpleIcon as UploadSimple, TableIcon as Table, CheckCircleIcon as CheckCircle, WarningIcon as Warning, FileCsvIcon as FileCsv, CrownIcon as Crown } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import { isNativeApp } from "@/lib/nativeBridge";

export default function ImportDogs() {
  const fileRef = useRef(null);
  const [quota, setQuota] = useState(null);
  const [parse, setParse] = useState(null);  // dry-run result
  const [busy, setBusy] = useState(false);
  const [committed, setCommitted] = useState(null);

  const refreshQuota = async () => {
    try {
      const r = await api.get("/import/dogs/quota");
      setQuota(r.data);
    } catch {/* */}
  };
  useEffect(() => { refreshQuota(); }, []);

  const submitFile = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      toast.error("Upload a .csv file");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/import/dogs/dry-run", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setParse(r.data);
      setCommitted(null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    if (!parse?.parse_id) return;
    setBusy(true);
    try {
      const r = await api.post("/import/dogs/commit", { parse_id: parse.parse_id });
      setCommitted(r.data);
      setParse(null);
      refreshQuota();
      toast.success(`Imported ${r.data.rows_imported} dogs`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const isLocked = quota?.limit === 0;

  return (
    <AppShell>
      <div className="mb-10">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B] mb-2">Tools</p>
        <h1 className="font-spectral text-4xl sm:text-5xl tracking-tight">Bulk import dogs</h1>
        <p className="text-[#6B6B6B] mt-2 max-w-2xl">
          Upload a CSV to register dozens (or hundreds) of dogs at once. We dry-run first so you can check for errors before any data is written.
        </p>
      </div>

      {quota && (
        <div className="bg-white border border-[#E5E5E5] rounded-xl p-5 mb-6 flex items-center justify-between flex-wrap gap-3" data-testid="import-quota-card">
          <div className="text-sm">
            <span className="text-xs uppercase tracking-wider text-[#6B6B6B]">Plan</span>
            <span className="ml-2 font-medium capitalize">{quota.tier || "Free"}</span>
            {quota.limit !== -1 && (
              <span className="text-[#6B6B6B] ml-3">
                {quota.used} / {quota.limit} rows used this month
              </span>
            )}
            {quota.limit === -1 && <span className="text-[#6B6B6B] ml-3">Unlimited</span>}
          </div>
        </div>
      )}

      {isLocked && (
        <div className="bg-white border border-dashed border-[#C9A227] rounded-xl p-8 text-center max-w-2xl">
          <Crown size={36} weight="duotone" className="text-gold mx-auto mb-3" />
          <h2 className="font-spectral text-xl mb-2">Bulk import requires a breeder plan</h2>
          {!isNativeApp() && (
            <p className="text-[#6B6B6B] text-sm mb-4">Basic: 50 rows/mo · Pro: 500 rows/mo · Kennel Elite: 5,000 rows/mo</p>
          )}
          <Button asChild className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white">
            <Link to="/billing" data-testid="import-upgrade-btn">{isNativeApp() ? "My account" : "Upgrade plan"}</Link>
          </Button>
        </div>
      )}

      {!isLocked && !parse && !committed && (
        <div className="bg-white border border-[#E5E5E5] rounded-xl p-8 max-w-2xl">
          <div className="border-2 border-dashed border-[#E5E5E5] rounded-xl p-10 text-center" data-testid="import-dropzone">
            <FileCsv size={48} weight="duotone" className="text-[#6B6B6B]/40 mx-auto mb-3" />
            <p className="text-ink font-medium mb-1">Upload your CSV</p>
            <p className="text-xs text-[#6B6B6B] mb-5">Required columns: name, breed, sex. Optional: dob, color, microchip, registration_number, sire_name, dam_name, notes, photo_url.</p>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => submitFile(e.target.files?.[0])}
              data-testid="import-file-input"
            />
            <Button onClick={() => fileRef.current?.click()} disabled={busy} className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white" data-testid="import-pick-file-btn">
              <UploadSimple size={16} className="mr-1.5" weight="bold" /> {busy ? "Parsing…" : "Choose CSV"}
            </Button>
          </div>

          <details className="mt-5 text-sm">
            <summary className="cursor-pointer text-ink font-medium">CSV format example</summary>
            <pre className="bg-[#FAFAFA] border border-[#E5E5E5] rounded p-3 text-xs overflow-x-auto mt-2 font-mono">
{`name,breed,sex,dob,color,microchip,registration_number,sire_name,dam_name,notes
Buddy,Labrador,male,2022-01-15,Yellow,985112345,,,Buddy Mom,
Luna,Labrador,female,2022-01-15,Black,985112346,,Buddy,Luna Mom,Sister to Buddy`}
            </pre>
          </details>
        </div>
      )}

      {parse && !committed && (
        <div className="bg-white border border-[#E5E5E5] rounded-xl p-6" data-testid="import-preview-card">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h2 className="font-spectral text-2xl tracking-tight">Preview</h2>
            <div className="flex items-center gap-3 text-sm">
              <Stat label="Total" value={parse.rows_total} />
              <Stat label="Valid" value={parse.rows_valid} good />
              <Stat label="Skipped" value={parse.rows_skipped} bad={parse.rows_skipped > 0} />
            </div>
          </div>
          {parse.over_quota && (
            <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-sm text-yellow-900 mb-3">
              This import has more rows ({parse.rows_valid}) than your remaining quota ({parse.quota?.remaining}). Upgrade or reduce rows.
            </div>
          )}
          {!!parse.errors?.length && (
            <details className="mb-4">
              <summary className="cursor-pointer text-sm text-red-700 font-medium">View {parse.errors.length} errors</summary>
              <div className="bg-red-50 border border-red-200 rounded p-3 mt-2 max-h-48 overflow-y-auto text-xs">
                {parse.errors.map((e, i) => (
                  <div key={i}>Row {e.row}: <strong>{e.error}</strong> {e.name && `(${e.name})`}</div>
                ))}
              </div>
            </details>
          )}
          <div className="border border-[#E5E5E5] rounded overflow-hidden mb-4">
            <table className="w-full text-sm">
              <thead className="bg-[#FAFAFA] text-xs uppercase text-[#6B6B6B]">
                <tr>
                  <th className="px-3 py-2 text-left">Name</th><th className="px-3 py-2 text-left">Breed</th>
                  <th className="px-3 py-2 text-left">Sex</th><th className="px-3 py-2 text-left">DOB</th>
                  <th className="px-3 py-2 text-left">Sire</th><th className="px-3 py-2 text-left">Dam</th>
                </tr>
              </thead>
              <tbody>
                {(parse.preview || []).map((r, i) => (
                  <tr key={i} className="border-t border-[#E5E5E5]">
                    <td className="px-3 py-2">{r.name}</td>
                    <td className="px-3 py-2">{r.breed}</td>
                    <td className="px-3 py-2">{r.sex}</td>
                    <td className="px-3 py-2">{r.dob || "—"}</td>
                    <td className="px-3 py-2">{r.sire_name || "—"}</td>
                    <td className="px-3 py-2">{r.dam_name || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {parse.rows_valid > 10 && <p className="text-xs text-[#6B6B6B] px-3 py-2">Showing first 10 of {parse.rows_valid} rows.</p>}
          </div>
          <div className="flex gap-2">
            <Button onClick={commit} disabled={busy || parse.over_quota || parse.rows_valid === 0} className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white" data-testid="import-commit-btn">
              {busy ? "Importing…" : `Import ${parse.rows_valid} dogs`}
            </Button>
            <Button onClick={() => setParse(null)} variant="outline" className="rounded-full border-[#E5E5E5]">Cancel</Button>
          </div>
        </div>
      )}

      {committed && (
        <div className="bg-white border border-green-200 rounded-xl p-6" data-testid="import-result-card">
          <CheckCircle size={32} weight="fill" className="text-green-600 mb-2" />
          <h2 className="font-spectral text-2xl tracking-tight">Imported {committed.rows_imported} dogs</h2>
          {committed.rows_failed > 0 && (
            <p className="text-sm text-yellow-800 mt-1">{committed.rows_failed} rows failed during commit. Check errors below.</p>
          )}
          {!!committed.errors?.length && (
            <details className="mt-4">
              <summary className="cursor-pointer text-sm text-red-700 font-medium">{committed.errors.length} commit errors</summary>
              <pre className="bg-red-50 border border-red-200 rounded p-3 mt-2 text-xs overflow-x-auto">
                {JSON.stringify(committed.errors, null, 2)}
              </pre>
            </details>
          )}
          <div className="flex gap-2 mt-5">
            <Button asChild className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white">
              <Link to="/dogs">View my dogs</Link>
            </Button>
            <Button onClick={() => { setCommitted(null); setParse(null); }} variant="outline" className="rounded-full">Import another file</Button>
          </div>
        </div>
      )}
    </AppShell>
  );
}

function Stat({ label, value, good, bad }) {
  const cls = good ? "text-green-700" : bad ? "text-red-700" : "text-[#0E0E0E]";
  return (
    <span className="inline-flex items-center gap-1">
      <span className="text-xs uppercase tracking-wider text-[#6B6B6B]">{label}:</span>
      <span className={`font-medium ${cls}`}>{value}</span>
    </span>
  );
}
